import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";
import {
  BlogPost,
  Category,
  Tag,
  CreateBlogPostPayload,
  UpdateBlogPostPayload,
} from "../types/blog";

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

export const blogService = {
  // GET: Fetch list of blog posts (with fallback to public post list)
  async getPosts(signal?: AbortSignal): Promise<BlogPost[]> {
    const client = getClient();

    // 1. Try author posts endpoint (authorized)
    try {
      const response = await client.get("/blog/post", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const data = response.data;
        const posts = Array.isArray(data) ? data : data?.results || [];
        if (posts.length > 0) return posts;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      // Continue to public fallback
    }

    // 2. Try public post-list endpoint
    try {
      const response = await client.get("/blog/post-list", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const data = response.data;
        const posts = Array.isArray(data) ? data : data?.results || [];
        return posts;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      console.warn("Blog posts fetch error:", err);
    }

    // 3. Fallback try django_drf_blog_api prefix
    try {
      const response = await client.get("/django_drf_blog_api/post-list", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const data = response.data;
        return Array.isArray(data) ? data : data?.results || [];
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
    }

    return [];
  },

  // GET: Fetch single blog post by ID or Slug
  async getPostById(idOrSlug: number | string, signal?: AbortSignal): Promise<BlogPost> {
    const posts = await this.getPosts(signal);
    const found = posts.find((p) => p.id === Number(idOrSlug) || p.slug === String(idOrSlug));
    if (found) return found;

    const client = getClient();
    try {
      const response = await client.get(`/blog/post-list/${idOrSlug}`, { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        return response.data;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      console.warn(`Direct fetch for post ${idOrSlug} failed:`, err);
    }

    throw new Error(`Article #${idOrSlug} not found.`);
  },

  // POST: Create a new blog post
  async createPost(data: CreateBlogPostPayload): Promise<BlogPost> {
    const client = getClient();
    // Prepare unified payload matching both 0.1.5 and 0.3.0 models
    const payload = {
      ...data,
      image: data.image || data.featured_image_url || undefined,
      image_caption: data.image_caption || data.featured_image_url_caption || undefined,
      featured_image_url: data.featured_image_url || data.image || undefined,
      featured_image_url_caption: data.featured_image_url_caption || data.image_caption || undefined,
    };

    let response;
    try {
      response = await client.post("/blog/post", payload);
    } catch (err: any) {
      // Fallback try with django_drf_blog_api
      response = await client.post("/django_drf_blog_api/post", payload);
    }

    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to create article (HTTP ${response?.status || "Unknown"})`);
  },

  // PUT: Update an existing blog post
  async updatePost(data: UpdateBlogPostPayload): Promise<BlogPost> {
    const client = getClient();
    const payload = {
      ...data,
      image: data.image || data.featured_image_url || undefined,
      image_caption: data.image_caption || data.featured_image_url_caption || undefined,
      featured_image_url: data.featured_image_url || data.image || undefined,
      featured_image_url_caption: data.featured_image_url_caption || data.image_caption || undefined,
    };

    let response;
    try {
      response = await client.put("/blog/post", payload);
    } catch (err: any) {
      response = await client.put("/django_drf_blog_api/post", payload);
    }

    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to update article #${data.id} (HTTP ${response?.status || "Unknown"})`);
  },

  // DELETE: Delete a blog post
  async deletePost(id: number): Promise<void> {
    const client = getClient();
    let response;
    try {
      response = await client.delete("/blog/post", { data: { id } });
    } catch (err: any) {
      response = await client.delete("/django_drf_blog_api/post", { data: { id } });
    }

    if (response && (response.status === 200 || response.status === 204)) {
      return;
    }
    throw new Error(`Failed to delete article #${id} (HTTP ${response?.status || "Unknown"})`);
  },

  // GET: Fetch blog categories
  async getCategories(signal?: AbortSignal): Promise<Category[]> {
    const client = getClient();
    try {
      const response = await client.get("/blog/category", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        return Array.isArray(response.data) ? response.data : response.data?.results || [];
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      try {
        const fbRes = await client.get("/django_drf_blog_api/category", { signal });
        if (fbRes && (fbRes.status === 200 || fbRes.status === 201)) {
          return Array.isArray(fbRes.data) ? fbRes.data : fbRes.data?.results || [];
        }
      } catch (e) {
        console.warn("Failed to load categories:", e);
      }
    }
    return [];
  },

  // GET: Fetch blog tags
  async getTags(signal?: AbortSignal): Promise<Tag[]> {
    const client = getClient();
    try {
      const response = await client.get("/blog/tags", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        return Array.isArray(response.data) ? response.data : response.data?.results || [];
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      try {
        const fbRes = await client.get("/django_drf_blog_api/tags", { signal });
        if (fbRes && (fbRes.status === 200 || fbRes.status === 201)) {
          return Array.isArray(fbRes.data) ? fbRes.data : fbRes.data?.results || [];
        }
      } catch (e) {
        console.warn("Failed to load tags:", e);
      }
    }
    return [];
  },
};
